import { useCallback, useMemo, useRef, useState, ChangeEvent } from 'react';
import { IconButton, Tooltip } from '@radix-ui/themes';
import { ImageIcon, VideoIcon, UpdateIcon } from '@radix-ui/react-icons';
import type { Editor } from '@tiptap/react';
import type { ComponentType } from 'react';
import { toast } from 'sonner';
import { useUploadSnippetAttachment } from '@/v2/entities/KnowledgeSnippet';
import { validateMediaFile, getAcceptedImageTypes, getAcceptedVideoTypes } from '../utils/fileValidation';

type MediaVariant = 'image' | 'video';

type MediaUploadConfig = {
  tooltip: string;
  loadingText: string;
  successText: string;
  errorText: string;
  invalidTypeText: string;
  getAcceptValue: () => string;
  Icon: ComponentType<{ className?: string }>;
  canInsert: (editor: Editor) => boolean;
  insertNode: (editor: Editor, src: string) => void;
  isMatchingType: (mediaType?: string) => boolean;
};

const mediaConfig: Record<MediaVariant, MediaUploadConfig> = {
  image: {
    tooltip: 'Upload image',
    loadingText: 'Uploading image…',
    successText: 'Image uploaded successfully',
    errorText: 'Failed to upload image. Please try again.',
    invalidTypeText: 'Please select an image file',
    getAcceptValue: getAcceptedImageTypes,
    Icon: ImageIcon,
    canInsert: (editor) => editor.can().setImage({ src: '' }),
    insertNode: (editor, src) => {
      editor.chain().focus().setImage({ src }).run();
    },
    isMatchingType: (mediaType) => mediaType === 'image',
  },
  video: {
    tooltip: 'Upload video',
    loadingText: 'Uploading video…',
    successText: 'Video uploaded successfully',
    errorText: 'Failed to upload video. Please try again.',
    invalidTypeText: 'Please select a video file',
    getAcceptValue: getAcceptedVideoTypes,
    Icon: VideoIcon,
    canInsert: (editor) => editor.can().setVideo({ src: '' }),
    insertNode: (editor, src) => {
      editor.chain().focus().setVideo({ src }).run();
    },
    isMatchingType: (mediaType) => mediaType === 'video',
  },
};

interface MediaUploadButtonProps {
  editor: Editor;
  snippetId: string;
  variant: MediaVariant;
}

export function MediaUploadButton({ editor, snippetId, variant }: MediaUploadButtonProps) {
  const config = useMemo(() => mediaConfig[variant], [variant]);
  const acceptValue = useMemo(() => config.getAcceptValue(), [config]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: uploadAttachment } = useUploadSnippetAttachment();

  const handleFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      const validation = validateMediaFile(file);
      if (!validation.valid) {
        if (validation.error) {
          toast.error(validation.error);
        }
        return;
      }

      if (!config.isMatchingType(validation.mediaType)) {
        toast.error(config.invalidTypeText);
        return;
      }

      try {
        setIsUploading(true);

        const uploadPromise = uploadAttachment({
          snippetId,
          file,
        });

        toast.promise(uploadPromise, {
          loading: config.loadingText,
          success: config.successText,
          error: config.errorText,
        });

        const publicUrl = await uploadPromise;
        config.insertNode(editor, publicUrl);
      } catch (error) {
        console.error('Failed to upload media:', error);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [config, editor, snippetId, uploadAttachment]
  );

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const { Icon } = config;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptValue}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <Tooltip content={config.tooltip}>
        <IconButton
          size="1"
          variant="ghost"
          onClick={handleButtonClick}
          disabled={isUploading || !config.canInsert(editor)}
        >
          {isUploading ? <UpdateIcon className="animate-spin" /> : <Icon />}
        </IconButton>
      </Tooltip>
    </>
  );
}
